import { Grid } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";
import { useMemo } from "react";
import { useParams } from "react-router-dom";
import * as Yup from "yup";
import AsyncSelect from "../../../../../../../../components/AsyncSelect";
import CustomDatePicker from "../../../../../../../../components/CustomDatePicker";
import Input from "../../../../../../../../components/Input";
import DropZone from "../../../../../../../../components/app/DropZone";
import StyledFormModal from "../../../../../../../../components/globalModal/StyledModal";
import StyledButtonV2 from "../../../../../../../../components/app/StyledButtonV2";
import { Option } from "../../../../../../../../constants/common";
import { DisclosureHost } from "../../../../../../../../hooks/use-disclosure";
import { useSnackbar } from "../../../../../../../../hooks/useSnackbar";
import { useGetOrders } from "../../../../../../../../queries/patient-chart/order/use-get-orders";
import { useCreateOrderResult } from "../../../../../../../../queries/patient-chart/result/use-create-result";
import { UploadLabResultAbnormalFlagEnum } from "../../../../../../../../services";
import {
  DEF_PAGINATION,
  showToastOnApiCall,
  createMapper,
  getXtenantId,
} from "../../../../../../../../utils/utils";
import { DOCUMENT_FORMAT_MAPPER } from "../../document/document-constant";
import { useUpdateOrderResult } from "../../../../../../../../queries/patient-chart/result/use-update-result";
import dayjs from "dayjs";
import { ORDER_STATUS_OPTIONS } from "@/pages/app/provider/scheduling/appointment/encounter/default-forms/UpsertLabRequisitionForm";

interface UpsertOrderProps {
  disclosureHost: DisclosureHost;
}

const UpsertResult = ({ disclosureHost }: UpsertOrderProps) => {
  const { patientId } = useParams();

  const { mutateAsync: createLabResult } = useCreateOrderResult();
  const { mutateAsync: updateLabResult } = useUpdateOrderResult();

  const showSnackbar = useSnackbar();
  const { data: orders } = useGetOrders({
    patientUuid: patientId ?? "",
    archive: false,
    orderStatus: ORDER_STATUS_OPTIONS.CANCELLED,
    pagination: DEF_PAGINATION,
    xTENANTID: getXtenantId(),
    enabled: disclosureHost.opened,
  });
  const queryClient = useQueryClient();

  const ordersOptions: Option[] = useMemo(() => {
    return (
      orders?.data?.content?.map((it) => ({
        value: it.uuid,
        label: it.orderTitle || "-",
      })) ?? []
    );
  }, [orders?.data?.content]);

  const validationSchema = Yup.object({
    patientOrderId: Yup.object().required("Order is required"),
    recordedDate: Yup.string().required("Recorded Date is required"),
    abnormalFlag: Yup.object().required("Abnormal Flag is required"),
    // resultValue: Yup.string().trim().required("Result Value is required"),
  });

  const resetAndClose = () => {
    formik.resetForm();
    disclosureHost.close();
  };

  const saveOrderResult = (values: any) => {
    const payload = {
      ...values,
      abnormalFlag: values?.abnormalFlag?.value,
      patientOrderId: values?.patientOrderId?.value,
      patientId: patientId ?? "",
      xTENANTID: getXtenantId(),
    };
    if (disclosureHost.data?.uuid)
      showToastAndResetForm(updateLabResult(payload));
    else showToastAndResetForm(createLabResult(payload));
  };

  async function showToastAndResetForm(apiCall: Promise<any>) {
    showToastOnApiCall({
      apiCall,
      showSnackbar,
      onSave: () => {
        queryClient.invalidateQueries({
          queryKey: ["patient-orders-results-list"],
        });
        queryClient.invalidateQueries({
          queryKey: ["patient-prescription-list"],
        });
        resetAndClose();
      },
    });
  }

  const formik = useFormik({
    initialValues: disclosureHost?.data || initialValues,
    validationSchema: validationSchema,
    onSubmit: saveOrderResult,
    enableReinitialize: true,
  });
  // console.log("formik.errors", formik.errors);
  // console.log("formik.values", formik.values);

  function handleClose() {
    formik.resetForm();
    disclosureHost.close();
  }

  return (
    <StyledFormModal
      open={disclosureHost.opened}
      onClose={handleClose}
      dialogTitle={
        disclosureHost.data?.uuid ? "Edit Lab Result" : "Add Lab Result"
      }
      dialogAction={
        <StyledButtonV2
          label="Save"
          variant="contained"
          onClick={formik.submitForm}
        />
      }
      sx={{
        "& .MuiDialog-paper": {
          width: "35vw",
          height: "60vh",
        },
      }}
    >
      <Grid container spacing={2} pt={2}>
        <Grid item xs={6}>
          <Input
            label="Lab Description"
            textFieldProps={{
              name: "labDescription",
              value: formik.values?.labDescription,
              onChange: formik.handleChange,
              placeholder: "Enter Lab Description",
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <AsyncSelect
            label="Orders"
            required
            value={formik.values?.patientOrderId}
            options={ordersOptions}
            onChange={(option: any) =>
              formik.setFieldValue("patientOrderId", option)
            }
            error={
              formik.touched.patientOrderId &&
              (formik.errors.patientOrderId as any)
            }
          />
        </Grid>
        <Grid item xs={3}>
          <Input
            label="Result Value"
            textFieldProps={{
              name: "resultValue",
              value: formik.values.resultValue,
              onChange: formik.handleChange,
              placeholder: "Enter Result Value",
            }}
          />
        </Grid>
        <Grid item xs={3}>
          <CustomDatePicker
            placeholder="Choose Date"
            label="Recorded Date"
            maxDate={dayjs(new Date())}
            required
            value={
              formik.values?.recordedDate
                ? formik.values?.recordedDate
                : undefined
            }
            onChange={(val) =>
              formik.setFieldValue("recordedDate", val?.toISOString())
            }
            error={
              formik.touched.recordedDate && (formik.errors.recordedDate as any)
            }
          />
        </Grid>
        <Grid item xs={6}>
          <AsyncSelect
            label="Abnormal Flag"
            required
            value={formik.values?.abnormalFlag}
            name="active"
            options={ABNORMAL_FLAG_OPTIONS}
            onChange={(option: any) =>
              formik.setFieldValue("abnormalFlag", option)
            }
            error={
              formik.touched.abnormalFlag && (formik.errors.abnormalFlag as any)
            }
          />
        </Grid>
        <Grid item xs={12} mt={2}>
          <Input
            label="Note"
            textFieldProps={{
              placeholder: "Enter Note",
              rows: 3,
              multiline: true,
              name: "note",
              onChange: formik.handleChange,
              value: formik.values.note,
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <DropZone
            error={formik.touched.file && (formik.errors.file as any)}
            supportedFile="JPEG, PNG, GIF, MP4, PDF, PSD, AI, Word, PPT"
            value={
              (formik.values.labDocument as any) ||
              (formik.values.fileName as any)
            }
            content="Drag & Drop files Or Browse Files"
            height={"190px"}
            onClick={(file) => {
              const curFile = file?.item(0);
              formik.setFieldValue("file", curFile ?? undefined);
              formik.setFieldValue("fileName", curFile?.name ?? undefined);
              // Clear the existing document URL so removing/replacing the file
              // in edit mode actually takes effect (value falls back to fileName).
              formik.setFieldValue("labDocument", undefined);
              formik.setFieldValue(
                "documentFormat",
                curFile?.type ? DOCUMENT_FORMAT_MAPPER[curFile.type] : undefined
              );
            }}
            accept={supportedFileTypes?.join(",")}
          />
        </Grid>
      </Grid>
    </StyledFormModal>
  );
};

export default UpsertResult;

const supportedFileTypes = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "video/mp4",
  "application/pdf",
  "image/vnd.adobe.photoshop", // for PSD
  "application/postscript", // for AI
  "application/msword", // for Word
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // for Word (docx)
  "application/vnd.ms-powerpoint", // for PPT
  "application/vnd.openxmlformats-officedocument.presentationml.presentation", // for PPTX
];

const ABNORMAL_FLAG_OPTIONS: {
  value: UploadLabResultAbnormalFlagEnum;
  label: string;
}[] = [
  { value: "ABOVE_UPPER_PANIC_LIMITS", label: "Above Upper Panic Limits" },
  { value: "BELOW_LOWER_PANIC_LIMITS", label: "Above Lower Panic Limits" },
  { value: "BELOW_LOW_NORMAL", label: "Below Low Normal" },
];

export const ABNORMAL_FLAG_OPTIONS_MAPPER = createMapper(ABNORMAL_FLAG_OPTIONS);

const initialValues = {
  labDescription: "",
  patientOrderId: undefined,
  recordedDate: new Date(),
  abnormalFlag: undefined,
  note: "",
  file: undefined,
  resultValue: "",
};
